import { z } from 'zod';
import { GENERATED_CARD_MANIFEST } from './card-manifest.generated';
import { CARD_CONTENT_VERSION, isCardDefinitionId } from './cards';

/**
 * **O que o runtime sabe sobre a ARTE de uma carta** (gate V28H.F).
 *
 * Uma projecção gerada dos masters, escrita pelo pipeline e lida por quem
 * desenha. Liga, por definição: o id, a chave de arte, o hash do master, o hash
 * do derivado, as duas dimensões e o caminho servido.
 *
 * ## O que este contrato não pode carregar
 *
 * Cooldown, nível, raridade, modo, efeito, custo ou economia. Não há campo onde
 * coubessem, e isso é a garantia: **nada de jogo é inferido de uma imagem**. O
 * que uma carta É vive em `cards.ts`, decidido por pessoas.
 *
 * A raridade é o exemplo que mais tenta: ela decide a moldura, e seria cómodo
 * tê-la aqui ao lado do ficheiro. Seria também a segunda fonte de verdade sobre
 * a raridade - e a que ninguém actualiza. A interface lê-a do catálogo e
 * compõe-na por cima da arte.
 *
 * ## Vazio é um estado legítimo, e é o estado de hoje
 *
 * Os dezasseis masters ainda não existem. O manifesto gerado tem zero entradas,
 * o `shared` compila, o jogo constrói, e {@link cardArt} responde `null` - que é
 * exactamente o que o contrato de recuo abaixo espera.
 */

export const CARD_MANIFEST_VERSION = 1;

export const cardManifestEntrySchema = z
  .object({
    /** O id canónico da carta. É também o nome do ficheiro master. */
    cardId: z.string().min(1),
    /** A chave de arte. É o id: uma carta tem uma arte e uma arte só. */
    assetKey: z.string().min(1),
    /** O ficheiro autorado, sob `assets-source/cards/`. */
    master: z.string().min(1),
    masterSha256: z.string().regex(/^[0-9a-f]{64}$/),
    masterWidth: z.number().int().positive(),
    masterHeight: z.number().int().positive(),
    /** O caminho servido, relativo à raiz de assets. */
    file: z.string().min(1),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    /**
     * A versão do CONTEÚDO das cartas em que este derivado foi emitido.
     *
     * Não é a versão do manifesto: esta diz de que catálogo a arte veio, e é o
     * que permite descobrir um derivado emitido antes de uma mudança de
     * conteúdo sem comparar dezasseis hashes.
     */
    contentVersion: z.number().int().min(1),
  })
  .strict();

export type CardManifestEntry = z.infer<typeof cardManifestEntrySchema>;

export const cardManifestSchema = z
  .object({
    manifestVersion: z.number().int().min(1),
    entries: z.array(cardManifestEntrySchema),
  })
  .strict();

export type CardManifest = z.infer<typeof cardManifestSchema>;

/** O manifesto embutido, tal como o pipeline o escreveu. */
export const CARD_MANIFEST: CardManifest = GENERATED_CARD_MANIFEST;

const BY_ID: ReadonlyMap<string, CardManifestEntry> = new Map(
  CARD_MANIFEST.entries.map((entry) => [entry.cardId, entry]),
);

/**
 * **A arte desta carta, ou `null` quando ela ainda não existe.**
 *
 * ## O contrato de RECUO, e o que ele proíbe
 *
 * `null` não é um erro e não é um caso raro: hoje é a resposta para as
 * dezasseis. Quem desenha trata-o como o inventário já trata qualquer definição
 * sem arte - o recuo genérico de item sem imagem, o mesmo que
 * `MISSING_ART_LABEL` nomeia.
 *
 * Três coisas que uma superfície **nunca** pode fazer com este `null`:
 *
 * - **esconder a carta.** Uma carta sem arte continua a ser uma carta que o
 *   jogador tem, que pode fundir e que pode equipar. Escondê-la faria o
 *   inventário mentir sobre o que a conta possui;
 * - **partir.** Nem a célula de inventário, nem o aviso de queda, nem o espaço
 *   podem lançar, ficar em branco ou desaparecer por falta de uma imagem;
 * - **inventar um caminho.** Compor `cards/${id}.png` à mão produziria um
 *   pedido para um ficheiro que não está lá. O caminho vem daqui ou não vem de
 *   lado nenhum.
 *
 * Um id desconhecido devolve `null` pela mesma porta, e não atira: a interface
 * desenha o que o servidor lhe deu, e uma carta que este build não conhece não
 * deve derrubar um painel.
 */
export function cardArt(cardId: string): CardManifestEntry | null {
  return BY_ID.get(cardId) ?? null;
}

/** Se o pipeline já emitiu arte para esta carta. */
export function hasCardArt(cardId: string): boolean {
  return BY_ID.has(cardId);
}

/**
 * Se o manifesto descreve exactamente o catálogo: nem a mais, nem a menos.
 *
 * `true` também para o manifesto VAZIO, que é o estado de hoje - "nenhuma arte
 * emitida" é coerente com o catálogo, e "arte para quinze das dezasseis" não é.
 * O que isto recusa é uma entrada para um id que não existe e um manifesto
 * parcialmente emitido.
 */
export function cardManifestMatchesCatalog(manifest: CardManifest = CARD_MANIFEST): boolean {
  if (manifest.entries.length === 0) {
    return true;
  }
  const ids = manifest.entries.map((entry) => entry.cardId);
  if (new Set(ids).size !== ids.length) {
    return false;
  }
  if (!ids.every((id) => isCardDefinitionId(id))) {
    return false;
  }
  if (!manifest.entries.every((entry) => entry.contentVersion === CARD_CONTENT_VERSION)) {
    return false;
  }
  return ids.length === 16;
}
